// pages/public/AmbulanceDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Ambulance as AmbulanceIcon,
  Phone,
  MapPin,
  Star,
  Clock,
  CheckCircle,
  Shield,
  Truck,
  DollarSign,
  Info,
  ChevronLeft,
  AlertCircle,
  Users,
  Award,
  Navigation
} from 'lucide-react';
import ambulanceService from '../../services/ambulanceService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AmbulanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [ambulance, setAmbulance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);

  useEffect(() => {
    fetchAmbulanceDetails();
    getCurrentLocation();
  }, [id]);

  const getCurrentLocation = async () => {
    const result = await ambulanceService.getCurrentLocation();
    if (result.success) {
      setCurrentLocation(result.data);
    }
  };

  const fetchAmbulanceDetails = async () => {
    try {
      setLoading(true);
      const response = await ambulanceService.getById(id);
      
      if (response.success) {
        setAmbulance(response.data.ambulance);
      } else {
        setError(response.message || 'Failed to load ambulance details');
      }
    } catch (err) {
      console.error('Fetch ambulance error:', err);
      setError('Failed to load ambulance details');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAmbulance = () => {
    navigate('/ambulances/request', {
      state: {
        ambulance,
        pickupLocation: currentLocation
      }
    });
  };

  const handleCallNow = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner text="Loading ambulance details..." />
      </div>
    );
  }

  if (error || !ambulance) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2">
                  {error || 'Ambulance not found'}
                </h3>
                <button
                  onClick={() => navigate('/emergency')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Back to Emergency Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-8 text-white">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold mb-2">{ambulance.name}</h1>
                    {ambulance.description && (
                      <p className="text-red-100">{ambulance.description}</p>
                    )}
                  </div>
                  {ambulance.isVerified && (
                    <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Verified</span>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  {ambulance.rating?.average > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Star className="w-5 h-5 fill-white" />
                        <span className="text-2xl font-bold">
                          {ambulance.rating.average.toFixed(1)}
                        </span>
                      </div>
                      <p className="text-sm text-red-100">
                        {ambulance.rating.totalReviews} reviews
                      </p>
                    </div>
                  )}
                  {ambulance.stats?.completedTrips > 0 && (
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Award className="w-5 h-5" />
                        <span className="text-2xl font-bold">
                          {ambulance.stats.completedTrips}
                        </span>
                      </div>
                      <p className="text-sm text-red-100">Trips completed</p>
                    </div>
                  )}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Clock className="w-5 h-5" />
                      <span className="text-lg font-bold">
                        {ambulance.operatingHours?.is24Hours ? '24/7' : 'Limited'}
                      </span>
                    </div>
                    <p className="text-sm text-red-100">Availability</p>
                  </div>
                </div>
              </div>

              {/* Location */}
              {ambulance.location?.address && (
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Location</p>
                      <p className="text-gray-600">{ambulance.location.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Services */}
            {ambulance.services && ambulance.services.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AmbulanceIcon className="w-6 h-6 text-red-600" />
                  Services Offered
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {ambulance.services.map((service, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {service.name || service.type}
                      </h3>
                      {service.description && (
                        <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      )}
                      {service.basePrice > 0 && (
                        <p className="text-sm font-medium text-red-600">
                          From KES {service.basePrice.toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Capabilities */}
            {ambulance.capabilities && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-red-600" />
                  Medical Capabilities
                </h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: 'basicLifeSupport', label: 'Basic Life Support (BLS)' },
                    { key: 'advancedLifeSupport', label: 'Advanced Life Support (ALS)' },
                    { key: 'criticalCare', label: 'Critical Care Transport' },
                    { key: 'neonatalCare', label: 'Neonatal Care' },
                    { key: 'bariatricTransport', label: 'Bariatric Transport' }
                  ].map((capability) => (
                    <div
                      key={capability.key}
                      className={`flex items-center gap-2 p-3 rounded-lg border ${
                        ambulance.capabilities[capability.key]
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {ambulance.capabilities[capability.key] ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                      )}
                      <span
                        className={`text-sm font-medium ${
                          ambulance.capabilities[capability.key]
                            ? 'text-green-900'
                            : 'text-gray-500'
                        }`}
                      >
                        {capability.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment */}
            {ambulance.equipment && ambulance.equipment.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Truck className="w-6 h-6 text-red-600" />
                  Equipment Available
                </h2>
                <div className="grid md:grid-cols-3 gap-3">
                  {ambulance.equipment.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-gray-900">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Operating Hours */}
            {ambulance.operatingHours && !ambulance.operatingHours.is24Hours && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-red-600" />
                  Operating Hours
                </h2>
                <div className="space-y-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                    const hours = ambulance.operatingHours[day];
                    return (
                      <div key={day} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                        <span className="font-medium text-gray-700 capitalize">{day}</span>
                        {hours?.isClosed ? (
                          <span className="text-red-600 font-medium">Closed</span>
                        ) : (
                          <span className="text-gray-900">
                            {hours?.open || '00:00'} - {hours?.close || '23:59'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-red-600" />
                Pricing
              </h3>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Base Fee</p>
                  <p className="text-2xl font-bold text-gray-900">
                    KES {ambulance.pricing?.baseFee?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Per Kilometer</p>
                  <p className="text-2xl font-bold text-gray-900">
                    KES {ambulance.pricing?.perKmRate?.toLocaleString() || 0}
                  </p>
                </div>
                {ambulance.pricing?.waitingChargePerHour > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Waiting Charge/Hour</p>
                    <p className="text-xl font-bold text-gray-900">
                      KES {ambulance.pricing.waitingChargePerHour.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 space-y-3">
                <button
                  onClick={handleRequestAmbulance}
                  className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2"
                >
                  <AmbulanceIcon className="w-5 h-5" />
                  Request Ambulance
                </button>
                <button
                  onClick={() => handleCallNow(ambulance.contact?.phone || ambulance.contact?.emergency)}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-semibold flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </button>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-red-600" />
                Contact Information
              </h3>
              <div className="space-y-3">
                {ambulance.contact?.phone && (
                  <div>
                    <p className="text-sm text-gray-600">Primary Phone</p>
                    <a
                      href={`tel:${ambulance.contact.phone}`}
                      className="text-gray-900 font-medium hover:text-red-600"
                    >
                      {ambulance.contact.phone}
                    </a>
                  </div>
                )}
                {ambulance.contact?.emergency && (
                  <div>
                    <p className="text-sm text-gray-600">Emergency Hotline</p>
                    <a
                      href={`tel:${ambulance.contact.emergency}`}
                      className="text-red-600 font-bold hover:text-red-700"
                    >
                      {ambulance.contact.emergency}
                    </a>
                  </div>
                )}
                {ambulance.contact?.email && (
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <a
                      href={`mailto:${ambulance.contact.email}`}
                      className="text-gray-900 hover:text-red-600"
                    >
                      {ambulance.contact.email}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Fleet Info */}
            {ambulance.limits && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  Fleet Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Active Vehicles</span>
                    <span className="font-bold text-gray-900">
                      {ambulance.limits.currentVehicles}/{ambulance.limits.maxVehicles}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Drivers</span>
                    <span className="font-bold text-gray-900">
                      {ambulance.limits.currentDrivers}/{ambulance.limits.maxDrivers}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbulanceDetail;